import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import CtaFinal from "@/components/CtaFinal";
import { Reveal, MaskedLineInView, Eyebrow } from "@/components/Reveal";
import { IMG, PROCESS_STEPS } from "@/data/content";

const STEP_DETAILS = [
  "Veniamo a vedere l'immobile, ascoltiamo cosa vuoi realizzare e raccogliamo tutte le informazioni utili.",
  "Analizziamo lo stato dell'immobile e definiamo insieme le lavorazioni necessarie.",
  "Ricevi un preventivo chiaro, con la descrizione trasparente delle lavorazioni previste.",
  "Pianifichiamo le fasi, coordiniamo le maestranze e prepariamo il cantiere.",
  "Eseguiamo i lavori seguendo il programma, con un referente diretto sempre reperibile.",
  "Curiamo finiture e dettagli finali: sono quelli che fanno la differenza.",
  "Ti consegniamo l'immobile finito, verificando insieme ogni ambiente.",
];

const ChiaviInMano = () => (
  <>
    <SEO
      title="Ristrutturazioni Chiavi in Mano — SOPAJ | Un Unico Referente a Lecco e Brianza"
      description="Ristrutturazioni chiavi in mano: dal sopralluogo al preventivo, dall'organizzazione del cantiere alla consegna. Una sola regia per tutto. Interventi a Nibionno, Lecco, Brianza e in altre località."
      path="/chiavi-in-mano"
    />

    <section data-testid="cim-hero" className="max-w-7xl mx-auto px-5 md:px-8 pt-36 md:pt-48 pb-16 md:pb-24">
      <Reveal><Eyebrow number="01" label="Chiavi in mano" /></Reveal>
      <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-anthracite max-w-4xl">
        <MaskedLineInView>Chiavi in mano:</MaskedLineInView>
        <MaskedLineInView delay={0.1}>
          <span className="font-serif italic font-normal">il cantiere lo gestiamo noi.</span>
        </MaskedLineInView>
      </h1>
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <Reveal delay={0.2} className="lg:col-span-6">
          <p className="text-anthracite/75 leading-relaxed text-base md:text-lg">
            Demolizioni, murature, impianti, pavimenti, rivestimenti, cartongessi,
            tinteggiature: una ristrutturazione mette insieme molte lavorazioni
            diverse, ognuna con i suoi tempi e i suoi protagonisti.
          </p>
          <p className="mt-4 text-anthracite/75 leading-relaxed text-base md:text-lg">
            Con la formula chiavi in mano le coordiniamo tutte noi. Tu segui
            l'avanzamento e approvi le scelte; la gestione quotidiana del cantiere
            resta a noi, fino alla consegna delle chiavi.
          </p>
        </Reveal>
        <Reveal delay={0.3} className="lg:col-span-6">
          <img
            src={IMG.blueprint}
            alt="Pianificazione di una ristrutturazione chiavi in mano"
            className="w-full aspect-[16/9] object-cover"
            loading="lazy"
          />
        </Reveal>
      </div>
    </section>

    <section className="bg-sand/60">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-32">
        <Reveal><Eyebrow number="02" label="Il processo" /></Reveal>
        <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-anthracite max-w-2xl">
          <MaskedLineInView>Sette fasi, una sola regia.</MaskedLineInView>
        </h2>

        <ol className="mt-14 md:mt-20 relative">
          <div className="absolute left-[19px] md:left-1/2 top-0 bottom-0 w-px bg-anthracite/15" aria-hidden="true" />
          {PROCESS_STEPS.map((s, i) => (
            <Reveal key={s.n} delay={0.05}>
              <li
                data-testid={`cim-step-${s.n}`}
                className={`relative flex gap-6 md:gap-0 pb-12 md:pb-16 last:pb-0 ${
                  i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                }`}
              >
                <div className="shrink-0 w-10 md:w-auto md:absolute md:left-1/2 md:-translate-x-1/2 z-10">
                  <span className="block w-10 h-10 bg-terracotta text-ivory font-display text-sm flex items-center justify-center">
                    {s.n}
                  </span>
                </div>
                <div className={`md:w-1/2 ${i % 2 === 0 ? "md:pr-16 md:text-right" : "md:pl-16 md:ml-auto"}`}>
                  <h3 className="font-display text-xl md:text-2xl tracking-tight text-anthracite">{s.title}</h3>
                  <p className="mt-3 text-sm md:text-base text-concrete leading-relaxed max-w-md md:inline-block">
                    {STEP_DETAILS[i]}
                  </p>
                </div>
              </li>
            </Reveal>
          ))}
        </ol>

        <Reveal delay={0.15}>
          <div className="mt-16 text-center">
            <Link
              to="/contatti"
              data-testid="cim-cta-sopralluogo"
              className="group inline-flex items-center gap-3 bg-terracotta hover:bg-terracotta-hover text-ivory font-display text-sm tracking-[0.15em] uppercase px-10 py-5 transition-colors duration-300"
            >
              Prenota il sopralluogo
              <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>

    <CtaFinal />
  </>
);

export default ChiaviInMano;
